/** @jsxImportSource @emotion/react */
import React from "react";
import classnames from "classnames";
// @otg-one/ui_components/components
import { Tooltip } from "../index";
// @otg-one/ui_components/icons
import { Asteriks } from "../../icons";
import { Close, Search } from "../../sap_icons";
// @otg-one/ui_components/constants
import { Color } from "../../constants";
// Material UI Core
import FilledInput from "@mui/material/FilledInput";
import Typography from "@mui/material/Typography";
import { css } from "@emotion/react";

const useStyles = (props) => ({
  root: css({
    color: Color.neutral[900],
    fontFamily: "roboto",
    fontSize: "14px",
    borderRadius: "0px",
  }),
  grey: css({
    backgroundColor: `${Color.neutral[100]} !important`,
    "&:hover": {
      backgroundColor: Color.neutral[100],
    },
  }),
  white: css({
    backgroundColor: `${Color.neutral[0]} !important`,
    "&:hover": {
      backgroundColor: Color.neutral[0],
    },
  }),
  disabled: css({
    backgroundColor: `${Color.neutral[300]} !important`,
    color: Color.neutral[500],
    borderBottom: "none !important",
    pointerEvents: "none",
  }),
  focused: css({}),
  error: css({
    border: `2px solid ${Color.danger[400]}`,
    color: Color.neutral[900],
  }),
  errorText: css({
    marginTop: 3,
    fontFamily: "roboto",
    fontSize: `12px !important`,
    letterSpacing: 0.15,
    color: Color.danger[400],
  }),
  requiredIcon: css({
    position: "absolute",
    right: 12,
    top: 12,
    cursor: "pointer",
    "& svg": {
      width: 12,
      height: 12,
      color: Color.primary[500],
    },
  }),
  clearIcon: css({
    position: "absolute",
    right: 11,
    top: 13,
    cursor: "pointer",
    "& svg": {
      width: 18,
      height: 18,
      color: Color.neutral[500],
    },
    outline: "none",
  }),
  searchIcon: css({
    position: "absolute",
    left: 11,
    fontSize: 24,
    color: Color.neutral[500],
    outline: "none",
  }),
  input: css({
    padding: "12px 16px",
    transition: "all .3s ease",
    "&::placeholder": {
      opacity: 1,
      color: Color.neutral[400],
      fontFamily: "roboto",
      fontSize: "14px",
    },
    backgroundColor: "transparent",
  }),
  inputWithIconStart: css({
    padding: "12px 16px 12px 42px !important",
    transition: "all .3s ease",
    "&::placeholder": {
      opacity: 1,
      color: Color.neutral[400],
      fontFamily: "roboto",
      fontSize: "14px",
    },
    backgroundColor: "transparent",
  }),
  inputWithIconEnd: css({
    padding: "12px 16px 12px 16px",
    height: "20px",
    transition: "all .3s ease",
    "&::placeholder": {
      opacity: 1,
      color: Color.neutral[400],
      fontFamily: "roboto",
      fontSize: "14px",
    },
    backgroundColor: "transparent",
  }),
  inputBorder: css({
    "&:placeholder-shown": {
      borderBottom: "2px solid transparent !important",
    },
    borderBottom: `2px solid ${Color.primary[600]}`,
  }),
  multiline: css({
    padding: 0,
  }),
  adornedStart: css({
    padding: 0,
  }),
  adornedEnd: css({
    padding: 0,
  }),
});

const ErrorMessage = (props) => {
  const classes = useStyles();
  return <Typography css={classes.errorText}>{props.children}</Typography>;
};

export const Form = (props) => {
  const classes = useStyles(props);
  const {
    id,
    tabIndex = -1,
    icon,
    iconPlacement = "left",
    password = false,
    number = false,
    search = false,
    clear = false,
    required = false,
    color = "grey",
    placeholder = " ",
    value,
    disabled = false,
    onChange,
    onClick,
    onBlur,
    onKeyPress,
    onFocus,
    multiline = false,
    rows,
    error = false,
    errorMessage = "",
    onClear = () => {},
    style,
  } = props;

  const formClasses = {
    root: classnames({
      [classes.root]: true,
      [classes[color]]: color,
    }),
    disabled: classes.disabled,
    focused: classes.focused,
    error: classes.error,
    multiline: classes.multiline,
    input: classnames({
      [classes.input]: true,
      [classes.inputBorder]: !error,
      [classes.inputWithIconStart]: (icon !== null && iconPlacement === "left") || search === true,
    }),
    adornedStart: classes.adornedStart,
    adornedEnd: classes.adornedEnd,
  };

  let type = "text";
  if (password) {
    type = "password";
  } else if (number) {
    type = "number";
  }

  return (
    <>
      <FilledInput
        id={id}
        name={id}
        type={type}
        placeholder={placeholder}
        startAdornment={search ? <Search css={classes.searchIcon} /> : iconPlacement === "left" ? icon : <></>}
        endAdornment={
          required ? (
            <Tooltip title="Required" placement="right">
              <div css={classes.requiredIcon}>
                <Asteriks />
              </div>
            </Tooltip>
          ) : clear ? (
            <div css={classes.clearIcon} onClick={onClear}>
              <Close />
            </div>
          ) : iconPlacement === "right" ? (
            icon
          ) : (
            <></>
          )
        }
        required={required}
        value={value}
        onChange={onChange}
        onClick={onClick}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyPress={onKeyPress}
        disabled={disabled}
        classes={formClasses}
        multiline={multiline}
        rows={rows}
        fullWidth
        error={error}
        style={style}
        tabIndex={tabIndex}
        disableUnderline
      />
      <ErrorMessage>{errorMessage}</ErrorMessage>
    </>
  );
};

export default React.memo(Form);
